/**
 * useCallLogAccess
 *
 * Unified access gate for Call Logs.
 * Combines program-level permission (MenuKeys.CALL_LOGS / actions)
 * with the program-specific CallLog configuration stored in ProgramConfigurations.
 *
 * Use this hook in place of calling useProgramAccess +
 * callLogConfigService directly.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { useProgramAccess } from './useProgramAccess';
import { callLogConfigService } from '../services/callLogConfigService';
import { PROGRAM_ACTION_KEYS } from '../services/programAccessService';
import type { CallLogConfigurationDto } from '../types/callLogConfig';

const DEFAULT_ENTITY_TYPES = ['PATIENT', 'HCP', 'HOSPITAL', 'PHARMA', 'CASE', 'PROGRAM'];

export interface CallLogAccessResult {
  isLoading: boolean;
  config: CallLogConfigurationDto | null;
  /** Can the current user see the Call Logs section? */
  canView: boolean;
  /** Can the current user create a new call log? */
  canCreate: boolean;
  /** Can the current user edit an existing call log? */
  canEdit: boolean;
  /** Can the current user delete a call log? */
  canDelete: boolean;
  /** Can the current user view call log analytics? */
  canViewAnalytics: boolean;
  /** Can the current user view the follow-up worklist? */
  canViewWorklist: boolean;
  /** Can the current user export call logs? */
  canExport: boolean;
  /** Entity types enabled for this program */
  enabledEntityTypes: string[];
  /** Whether a published CALL_LOG_FORM has been configured */
  formConfigured: boolean;
}

export function useCallLogAccess(programId: string | undefined): CallLogAccessResult {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin';

  const { hasAction, isLoading: accessLoading } = useProgramAccess({ programId });

  const { data: configResponse, isLoading: configLoading } = useQuery({
    queryKey: ['callLogConfig', programId, user?.userId],
    queryFn: () => callLogConfigService.getConfiguration(programId!),
    enabled: !!programId && !!user && !isSuperAdmin,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const config = configResponse?.success ? configResponse.data : null;
  const isLoading = accessLoading || configLoading;

  return useMemo(() => {
    // SuperAdmin always has full access
    if (isSuperAdmin) {
      return {
        isLoading: false,
        config: null,
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canViewAnalytics: true,
        canViewWorklist: true,
        canExport: true,
        enabledEntityTypes: DEFAULT_ENTITY_TYPES,
        formConfigured: false,
      };
    }

    // While loading, assume safe defaults (no create/delete)
    if (isLoading && !config) {
      return {
        isLoading: true,
        config: null,
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canViewAnalytics: false,
        canViewWorklist: false,
        canExport: false,
        enabledEntityTypes: DEFAULT_ENTITY_TYPES,
        formConfigured: false,
      };
    }

    // Feature-level flag (defaults to enabled when not yet configured)
    const featureEnabled = config?.enabled ?? true;

    const canView = featureEnabled && hasAction(PROGRAM_ACTION_KEYS.CALL_LOG_VIEW);
    const canCreate =
      featureEnabled &&
      hasAction(PROGRAM_ACTION_KEYS.CALL_LOG_CREATE) &&
      (config?.allowCreate ?? true);
    const canEdit = featureEnabled && (config?.allowEdit ?? true);
    const canDelete = featureEnabled && (config?.allowDelete ?? false);
    const canViewAnalytics = featureEnabled && hasAction(PROGRAM_ACTION_KEYS.CALL_LOG_ANALYTICS_VIEW);
    const canViewWorklist = featureEnabled && hasAction(PROGRAM_ACTION_KEYS.CALL_LOG_FOLLOWUP_WORKLIST_VIEW);
    const canExport = featureEnabled && hasAction(PROGRAM_ACTION_KEYS.CALL_LOG_EXPORT);

    return {
      isLoading,
      config,
      canView,
      canCreate,
      canEdit,
      canDelete,
      canViewAnalytics,
      canViewWorklist,
      canExport,
      enabledEntityTypes: config?.enabledEntityTypes ?? DEFAULT_ENTITY_TYPES,
      formConfigured: config?.formConfigured ?? false,
    };
  }, [isLoading, config, isSuperAdmin, hasAction]);
}
